import React from 'react';

export interface VideoV3Track {
  src: string;
  lang: string;
  label?: string;
  default?: boolean;
}

export interface VideoV3Source {
  src: string;
  type?: string;
}

export interface EliceVideoV3Props {
  language?: 'en' | 'ko';
  source: VideoV3Source[];
  textTrack?: VideoV3Track[];
  poster?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  playbackRates?: number[];
  rememberPositionKey?: string;
  primaryColor?: string;
  customColor?: {
    background?: string;
    text?: string;
    mutedText?: string;
    controlBackground?: string;
    border?: string;
    error?: string;
    primary?: string;
    focus?: string;
  };
  className?: string;
  style?: React.CSSProperties;
}

const DEFAULT_RATES = [0.5, 1, 1.25, 1.5, 2];
const DOUBLE_TAP_INTERVAL_MS = 280;
const DOUBLE_TAP_SEEK_SECONDS = 10;

type GestureKind = 'play' | 'pause' | 'forward' | 'backward';

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type PiPVideoElement = HTMLVideoElement & {
  webkitSetPresentationMode?: (
    mode: 'inline' | 'fullscreen' | 'picture-in-picture'
  ) => void;
  webkitPresentationMode?: 'inline' | 'fullscreen' | 'picture-in-picture';
};

type PiPDocument = Document & {
  pictureInPictureEnabled?: boolean;
  pictureInPictureElement?: Element | null;
  exitPictureInPicture?: () => Promise<void>;
};

function IconPlay() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path fill="currentColor" d="M6 4v16l14-8z" />
    </svg>
  );
}

function IconPause() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path fill="currentColor" d="M6 4h5v16H6zM13 4h5v16h-5z" />
    </svg>
  );
}

function IconVolumeOn() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M3 10v4h4l5 4V6L7 10H3zm12.5 2a4.5 4.5 0 0 0-2.5-4.03v8.06A4.5 4.5 0 0 0 15.5 12zm0-9v2.06A7 7 0 0 1 20 12a7 7 0 0 1-4.5 6.94V21A9 9 0 0 0 22 12a9 9 0 0 0-6.5-9z"
      />
    </svg>
  );
}

function IconVolumeOff() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16.5 12c0 .77-.22 1.5-.6 2.11l1.45 1.45A5.95 5.95 0 0 0 18.5 12c0-1.45-.51-2.77-1.35-3.8L15.7 9.65c.5.66.8 1.48.8 2.35zM3 10v4h4l5 4V6L7 10H3zm16.19 11L2 3.81 3.41 2.4 21.6 20.59 20.19 22z"
      />
    </svg>
  );
}

function IconPiP() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M3 5h18v14H3V5zm16 12V7H5v10h14zm-1-6h-6v4h6v-4z"
      />
    </svg>
  );
}

function IconFullscreen() {
  return (
    <svg viewBox="8 8 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12.5 11.25H16.25C16.9141 11.25 17.5 11.8359 17.5 12.5C17.5 13.2031 16.9141 13.75 16.25 13.75H13.75V16.25C13.75 16.9531 13.1641 17.5 12.5 17.5C11.7969 17.5 11.25 16.9531 11.25 16.25V12.5C11.25 11.8359 11.7969 11.25 12.5 11.25ZM13.75 23.75V26.25H16.25C16.9141 26.25 17.5 26.8359 17.5 27.5C17.5 28.2031 16.9141 28.75 16.25 28.75H12.5C11.7969 28.75 11.25 28.2031 11.25 27.5V23.75C11.25 23.0859 11.7969 22.5 12.5 22.5C13.1641 22.5 13.75 23.0859 13.75 23.75ZM23.75 11.25H27.5C28.1641 11.25 28.75 11.8359 28.75 12.5V16.25C28.75 16.9531 28.1641 17.5 27.5 17.5C26.7969 17.5 26.25 16.9531 26.25 16.25V13.75H23.75C23.0469 13.75 22.5 13.2031 22.5 12.5C22.5 11.8359 23.0469 11.25 23.75 11.25ZM28.75 23.75V27.5C28.75 28.2031 28.1641 28.75 27.5 28.75H23.75C23.0469 28.75 22.5 28.2031 22.5 27.5C22.5 26.8359 23.0469 26.25 23.75 26.25H26.25V23.75C26.25 23.0859 26.7969 22.5 27.5 22.5C28.1641 22.5 28.75 23.0859 28.75 23.75Z"
      />
    </svg>
  );
}

function IconFullscreenExit() {
  return (
    <svg viewBox="8 8 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M17.5 12.5V16.25C17.5 16.9531 16.9141 17.5 16.25 17.5H12.5C11.7969 17.5 11.25 16.9531 11.25 16.25C11.25 15.5859 11.7969 15 12.5 15H15V12.5C15 11.8359 15.5469 11.25 16.25 11.25C16.9141 11.25 17.5 11.8359 17.5 12.5ZM12.5 22.5H16.25C16.9141 22.5 17.5 23.0859 17.5 23.75V27.5C17.5 28.2031 16.9141 28.75 16.25 28.75C15.5469 28.75 15 28.2031 15 27.5V25H12.5C11.7969 25 11.25 24.4531 11.25 23.75C11.25 23.0859 11.7969 22.5 12.5 22.5ZM25 12.5V15H27.5C28.1641 15 28.75 15.5859 28.75 16.25C28.75 16.9531 28.1641 17.5 27.5 17.5H23.75C23.0469 17.5 22.5 16.9531 22.5 16.25V12.5C22.5 11.8359 23.0469 11.25 23.75 11.25C24.4141 11.25 25 11.8359 25 12.5ZM23.75 22.5H27.5C28.1641 22.5 28.75 23.0859 28.75 23.75C28.75 24.4531 28.1641 25 27.5 25H25V27.5C25 28.2031 24.4141 28.75 23.75 28.75C23.0469 28.75 22.5 28.2031 22.5 27.5V23.75C22.5 23.0859 23.0469 22.5 23.75 22.5Z"
      />
    </svg>
  );
}

const styles = `
.elice-video-v3 {
  --v3-bg: #07080a;
  --v3-fg: #f7f9ff;
  --v3-muted: #b9c2d9;
  --v3-primary: #55d8ff;
  --v3-primary-strong: #14bde8;
  --v3-danger: #ff6b6b;
  --v3-control-bg: rgba(12, 14, 20, 0.72);
  --v3-border: rgba(255, 255, 255, 0.24);
  --v3-focus: #55d8ff;
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  background:
    radial-gradient(120% 120% at 100% 0%, rgba(85, 216, 255, 0.18), transparent 45%),
    radial-gradient(150% 110% at 0% 100%, rgba(69, 226, 159, 0.16), transparent 50%),
    var(--v3-bg);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.32);
  touch-action: manipulation;
}

.elice-video-v3 video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #020204;
}

.elice-video-v3__surface {
  position: absolute;
  inset: 0;
  cursor: pointer;
}

.elice-video-v3__gesture-hit {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 33.333%;
}

.elice-video-v3__gesture-hit--left { left: 0; }
.elice-video-v3__gesture-hit--center { left: 33.333%; }
.elice-video-v3__gesture-hit--right { right: 0; }

.elice-video-v3__top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 28%;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0));
}

.elice-video-v3__top-actions {
  position: absolute;
  top: 10px;
  right: 10px;
  display: none;
  align-items: center;
  gap: 6px;
  z-index: 9;
}

.elice-video-v3__loader {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 52px;
  height: 52px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 999px;
  animation: elice-v3-spin 0.9s linear infinite;
  pointer-events: none;
}

.elice-video-v3__error {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(88%, 440px);
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.74);
  border: 1px solid rgba(255, 107, 107, 0.65);
  color: #fff;
  font-size: 14px;
  line-height: 1.45;
  text-align: center;
}

.elice-video-v3__feedback {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  display: grid;
  place-items: center;
  width: 82px;
  height: 82px;
  border-radius: 999px;
  background: rgba(8, 10, 16, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.26);
  color: white;
  font-size: 22px;
  font-weight: 700;
  opacity: 0;
  animation: elice-v3-pop 380ms ease-out forwards;
}

.elice-video-v3__controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  padding: 8px 10px 10px;
  color: var(--v3-fg);
  background: linear-gradient(to top, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0));
  transition: opacity 180ms ease;
}

.elice-video-v3__timeline-wrap {
  padding: 8px 0;
  margin: -8px 0 6px 0;
  cursor: pointer;
}

.elice-video-v3__controls[data-hidden="true"] {
  opacity: 0;
  pointer-events: none;
}

.elice-video-v3__center-play {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 72px;
  height: 72px;
  border-radius: 999px;
  border: 1px solid var(--v3-border);
  background: var(--v3-control-bg);
  color: var(--v3-fg);
  display: grid;
  place-items: center;
  cursor: pointer;
  backdrop-filter: blur(8px);
  z-index: 8;
}

.elice-video-v3__center-play svg {
  width: 40px;
  height: 40px;
}

.elice-video-v3__timeline {
  width: 100%;
  margin: 0;
  height: 3px;
  border-radius: 999px;
  accent-color: var(--v3-primary);
  cursor: pointer;
}

.elice-video-v3__row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 6px;
}

.elice-video-v3__group {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.elice-video-v3__group--right {
  justify-content: flex-end;
}

.elice-video-v3__volume {
  width: 84px;
  accent-color: var(--v3-primary);
}

.elice-video-v3__time {
  min-width: 84px;
  font-size: 11px;
  color: var(--v3-muted);
  font-variant-numeric: tabular-nums;
}

.elice-video-v3__btn,
.elice-video-v3__select {
  border: 0;
  background: transparent;
  color: var(--v3-fg);
  border-radius: 8px;
  height: 36px;
  transition:
    border-color 160ms ease,
    background-color 160ms ease,
    transform 140ms ease;
}

.elice-video-v3__btn {
  width: 36px;
  min-width: 36px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.elice-video-v3__btn svg {
  width: 24px;
  height: 24px;
  display: block;
  flex: 0 0 auto;
}

.elice-video-v3__btn:hover,
.elice-video-v3__select:hover {
  background: rgba(255, 255, 255, 0.14);
}

.elice-video-v3__btn:active {
  transform: scale(0.96);
}

.elice-video-v3__btn:focus-visible,
.elice-video-v3__select:focus-visible,
.elice-video-v3 input[type="range"]:focus-visible {
  outline: 2px solid var(--v3-focus);
  outline-offset: 2px;
}

.elice-video-v3__select {
  font-size: 11px;
  padding: 0 4px;
  color: var(--v3-muted);
}

.elice-video-v3__hide-mobile {
  display: initial;
}

@media (max-width: 830px) {
  .elice-video-v3 { border-radius: 12px; }
  .elice-video-v3__controls { padding: 7px 8px 8px; }
  .elice-video-v3__time { min-width: 70px; font-size: 10px; }
  .elice-video-v3__btn { width: 34px; min-width: 34px; height: 34px; }
  .elice-video-v3__btn svg { width: 24px; height: 24px; }
  .elice-video-v3__volume { width: 68px; }
  .elice-video-v3__top-actions { display: flex; }
  .elice-video-v3__bottom-hide-small { display: none; }
}

@media (max-width: 560px) {
  .elice-video-v3__row { grid-template-columns: 1fr auto; gap: 4px; }
  .elice-video-v3__group {
    flex-wrap: nowrap;
    gap: 4px;
  }
  .elice-video-v3__group--right { justify-content: flex-end; }
  .elice-video-v3__btn,
  .elice-video-v3__select {
    height: 32px;
    font-size: 10px;
  }
  .elice-video-v3__btn { width: 32px; min-width: 32px; }
  .elice-video-v3__btn svg { width: 24px; height: 24px; }
  .elice-video-v3__volume {
    width: 60px;
  }
  .elice-video-v3__hide-mobile {
    display: none;
  }
  .elice-video-v3__center-play {
    width: 62px;
    height: 62px;
    border: 0;
  }
  .elice-video-v3__center-play svg {
    width: 38px;
    height: 38px;
  }
}

@keyframes elice-v3-spin {
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes elice-v3-pop {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.7); }
  35% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.06); }
}
`;

const t = (language: 'en' | 'ko') => {
  if (language === 'ko') {
    return {
      play: '재생',
      pause: '일시정지',
      mute: '음소거',
      unmute: '음소거 해제',
      fullscreen: '전체 화면',
      exitFullscreen: '전체 화면 해제',
      captions: '자막',
      captionsOff: '자막 끄기',
      pip: 'PIP',
      speed: '속도',
      loading: '로딩 중...',
      error: '비디오를 재생할 수 없습니다. 소스/네트워크를 확인해 주세요.',
      seek: '탐색',
      player: '비디오 플레이어',
    };
  }
  return {
    play: 'Play',
    pause: 'Pause',
    mute: 'Mute',
    unmute: 'Unmute',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    captions: 'Captions',
    captionsOff: 'Captions Off',
    pip: 'PiP',
    speed: 'Speed',
    loading: 'Loading...',
    error: 'Unable to play this video. Please check source/network.',
    seek: 'Seek',
    player: 'Video player',
  };
};

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
};

export function EliceVideoV3({
  language = 'ko',
  source,
  textTrack,
  poster,
  autoPlay = false,
  loop = false,
  muted = false,
  preload = 'metadata',
  playbackRates = DEFAULT_RATES,
  rememberPositionKey,
  primaryColor,
  customColor,
  className,
  style,
}: EliceVideoV3Props) {
  const labels = React.useMemo(() => t(language), [language]);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const hideControlsTimerRef = React.useRef<number | null>(null);
  const lastTapRef = React.useRef<{ at: number; x: number; y: number } | null>(
    null
  );
  const feedbackTimeoutRef = React.useRef<number | null>(null);

  // refs for stable callbacks (avoids re-registering video event listeners on every play/pause)
  const isPlayingRef = React.useRef(false);
  const textTrackRef = React.useRef(textTrack);
  const isDraggingTimelineRef = React.useRef(false);
  const isSeekingRef = React.useRef(false);
  const isMediaReloadingRef = React.useRef(false);
  const singleTapTimerRef = React.useRef<number | null>(null);

  const [isReady, setIsReady] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isBuffering, setIsBuffering] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(muted);
  const [volume, setVolume] = React.useState(1);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [playbackRate, setPlaybackRate] = React.useState(
    playbackRates.find(r => r === 1) ?? playbackRates[0] ?? 1
  );
  const [showControls, setShowControls] = React.useState(true);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [feedbackText, setFeedbackText] = React.useState('');
  const [selectedCaption, setSelectedCaption] = React.useState<string>('off');
  const [isHotkeyActive, setIsHotkeyActive] = React.useState(false);

  // keep refs in sync with state (synchronous update during render, no useEffect needed)
  isPlayingRef.current = isPlaying;
  textTrackRef.current = textTrack;

  const showFeedback = React.useCallback((kind: GestureKind) => {
    const textMap: Record<GestureKind, string> = {
      play: '▶',
      pause: '❚❚',
      forward: `+${DOUBLE_TAP_SEEK_SECONDS}s`,
      backward: `-${DOUBLE_TAP_SEEK_SECONDS}s`,
    };
    setFeedbackText(textMap[kind]);
    if (feedbackTimeoutRef.current !== null) {
      window.clearTimeout(feedbackTimeoutRef.current);
    }
    feedbackTimeoutRef.current = window.setTimeout(() => {
      setFeedbackText('');
    }, 360);
  }, []);

  const resetControlsTimer = React.useCallback(() => {
    if (!isPlayingRef.current) {
      setShowControls(true);
      return;
    }
    setShowControls(true);
    if (hideControlsTimerRef.current !== null) {
      window.clearTimeout(hideControlsTimerRef.current);
    }
    hideControlsTimerRef.current = window.setTimeout(() => {
      setShowControls(false);
    }, 2300);
  }, []);

  const seekBy = React.useCallback(
    (delta: number, withFeedback = true) => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      const next = Math.max(
        0,
        Math.min(
          (video.currentTime ?? 0) + delta,
          Number.isFinite(video.duration) ? video.duration : Infinity
        )
      );
      video.currentTime = next;
      setCurrentTime(next);
      if (withFeedback) {
        showFeedback(delta > 0 ? 'forward' : 'backward');
      }
    },
    [showFeedback]
  );

  const togglePlayPause = React.useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      void video
        .play()
        .then(() => {
          showFeedback('play');
        })
        .catch(() => {
          setErrorMessage(labels.error);
        });
      return;
    }
    video.pause();
    showFeedback('pause');
  }, [labels.error, showFeedback]);

  const toggleFullscreen = React.useCallback(() => {
    const wrapper = wrapperRef.current as FullscreenElement | null;
    const video = videoRef.current as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | null;
    const fsDocument = document as FullscreenDocument;
    if (!wrapper || !video) {
      return;
    }
    const activeElement =
      document.fullscreenElement ?? fsDocument.webkitFullscreenElement;
    if (activeElement) {
      if (document.exitFullscreen) {
        void document.exitFullscreen();
      } else {
        fsDocument.webkitExitFullscreen?.();
      }
      return;
    }
    if (wrapper.requestFullscreen) {
      void wrapper.requestFullscreen();
      return;
    }
    if (wrapper.webkitRequestFullscreen) {
      wrapper.webkitRequestFullscreen();
      return;
    }
    video.webkitEnterFullscreen?.();
  }, []);

  const togglePiP = React.useCallback(async () => {
    const video = videoRef.current as PiPVideoElement | null;
    const pipDocument = document as PiPDocument;
    if (!video) {
      return;
    }
    try {
      if (pipDocument.pictureInPictureElement) {
        await pipDocument.exitPictureInPicture?.();
        return;
      }
      if (
        pipDocument.pictureInPictureEnabled &&
        video.requestPictureInPicture
      ) {
        await video.requestPictureInPicture();
        return;
      }
      if (video.webkitSetPresentationMode) {
        const nextMode =
          video.webkitPresentationMode === 'picture-in-picture'
            ? 'inline'
            : 'picture-in-picture';
        video.webkitSetPresentationMode(nextMode);
      }
    } catch {
      setErrorMessage(labels.error);
    }
  }, [labels.error]);

  const applyCaption = React.useCallback((value: string) => {
    const video = videoRef.current;
    if (!video?.textTracks) {
      return;
    }
    setSelectedCaption(value);
    Array.from(video.textTracks).forEach(track => {
      const shouldShow = value !== 'off' && track.language === value;
      track.mode = shouldShow ? 'showing' : 'disabled';
    });
  }, []);

  const handleSurfacePointerDown: React.PointerEventHandler<HTMLDivElement> =
    React.useCallback(
      event => {
        const target = event.target as HTMLElement | null;
        if (target?.closest('[data-video-v3-control="true"]')) {
          return;
        }
        const now = event.timeStamp;
        const x = event.clientX;
        const y = event.clientY;
        const prev = lastTapRef.current;

        if (!prev || now - prev.at > DOUBLE_TAP_INTERVAL_MS) {
          // 첫 번째 탭: 더블탭 대기 후 싱글탭으로 확정되면 play/pause
          lastTapRef.current = { at: now, x, y };
          resetControlsTimer();
          if (singleTapTimerRef.current !== null) {
            window.clearTimeout(singleTapTimerRef.current);
          }
          singleTapTimerRef.current = window.setTimeout(() => {
            singleTapTimerRef.current = null;
            lastTapRef.current = null;
            togglePlayPause();
          }, DOUBLE_TAP_INTERVAL_MS);
          return;
        }

        // 더블탭 확정: 싱글탭 타이머 취소
        if (singleTapTimerRef.current !== null) {
          window.clearTimeout(singleTapTimerRef.current);
          singleTapTimerRef.current = null;
        }

        const dx = x - prev.x;
        const dy = y - prev.y;
        const isNear = dx * dx + dy * dy <= 42 * 42;
        lastTapRef.current = null;
        if (!isNear) {
          return;
        }

        event.preventDefault();
        const wrapper = wrapperRef.current;
        if (!wrapper) {
          return;
        }
        const bounds = wrapper.getBoundingClientRect();
        const leftLimit = bounds.left + bounds.width * 0.33;
        const rightLimit = bounds.left + bounds.width * 0.67;
        if (x <= leftLimit) {
          seekBy(-DOUBLE_TAP_SEEK_SECONDS);
          return;
        }
        if (x >= rightLimit) {
          seekBy(DOUBLE_TAP_SEEK_SECONDS);
          return;
        }
        togglePlayPause();
      },
      [resetControlsTimer, seekBy, togglePlayPause]
    );

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.volume = volume;
    video.muted = isMuted;
    video.playbackRate = playbackRate;
  }, [isMuted, playbackRate, volume]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const onLoadedMetadata = () => {
      isMediaReloadingRef.current = false;
      setIsReady(true);
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
      if (rememberPositionKey) {
        const saved = Number(window.localStorage.getItem(rememberPositionKey));
        if (Number.isFinite(saved) && saved > 0 && saved < video.duration - 1) {
          video.currentTime = saved;
          setCurrentTime(saved);
        }
      }
      const defaultTrack = textTrackRef.current?.find(track => track.default);
      if (defaultTrack) {
        applyCaption(defaultTrack.lang);
      }
    };

    const onTimeUpdate = () => {
      const time = video.currentTime || 0;
      // 타임라인 드래그 중에는 timeupdate가 currentTime을 덮어쓰지 않도록 방지
      if (!isDraggingTimelineRef.current) {
        setCurrentTime(time);
      }
      if (rememberPositionKey) {
        const shouldSkipRememberWrite =
          isDraggingTimelineRef.current ||
          isSeekingRef.current ||
          isMediaReloadingRef.current ||
          !Number.isFinite(time) ||
          time < 0.25;
        if (!shouldSkipRememberWrite) {
          window.localStorage.setItem(rememberPositionKey, `${time}`);
        }
      }
    };

    const onSeeking = () => {
      isSeekingRef.current = true;
    };

    const onSeeked = () => {
      isSeekingRef.current = false;
    };

    const onLoadStart = () => {
      isMediaReloadingRef.current = true;
    };

    const onEmptied = () => {
      isMediaReloadingRef.current = true;
    };

    const onPlay = () => {
      setIsPlaying(true);
      setIsBuffering(false);
      resetControlsTimer();
    };

    const onPause = () => {
      setIsPlaying(false);
      setShowControls(true);
    };

    const onWaiting = () => {
      setIsBuffering(true);
    };

    const onPlaying = () => {
      isMediaReloadingRef.current = false;
      setIsBuffering(false);
    };

    const onError = () => {
      setErrorMessage(labels.error);
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('loadstart', onLoadStart);
    video.addEventListener('emptied', onEmptied);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onPause);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onWaiting);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('canplay', onPlaying);
    video.addEventListener('error', onError);

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('loadstart', onLoadStart);
      video.removeEventListener('emptied', onEmptied);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onPause);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onWaiting);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('canplay', onPlaying);
      video.removeEventListener('error', onError);
    };
  }, [applyCaption, labels.error, rememberPositionKey, resetControlsTimer]);

  React.useEffect(() => {
    const fsDocument = document as FullscreenDocument;
    const onFullscreenChange = () => {
      const activeElement =
        document.fullscreenElement ?? fsDocument.webkitFullscreenElement;
      setIsFullscreen(Boolean(activeElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener(
      'webkitfullscreenchange',
      onFullscreenChange as EventListener
    );

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        onFullscreenChange as EventListener
      );
    };
  }, []);

  React.useEffect(() => {
    return () => {
      if (hideControlsTimerRef.current !== null) {
        window.clearTimeout(hideControlsTimerRef.current);
      }
      if (feedbackTimeoutRef.current !== null) {
        window.clearTimeout(feedbackTimeoutRef.current);
      }
      if (singleTapTimerRef.current !== null) {
        window.clearTimeout(singleTapTimerRef.current);
      }
    };
  }, []);

  const handleHotkey = React.useCallback(
    (event: {
      key: string;
      code?: string;
      metaKey?: boolean;
      ctrlKey?: boolean;
      altKey?: boolean;
      preventDefault: () => void;
      stopPropagation?: () => void;
      stopImmediatePropagation?: () => void;
    }) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return false;
      }

      const stopEvent = () => {
        event.preventDefault();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
      };

      const key = event.key.toLowerCase();
      const code = event.code?.toLowerCase();
      switch (key) {
        case ' ':
        case 'k':
          stopEvent();
          togglePlayPause();
          return true;
        case 'arrowleft':
          stopEvent();
          seekBy(-5, false);
          return true;
        case 'arrowright':
          stopEvent();
          seekBy(5, false);
          return true;
        case 'm':
          stopEvent();
          setIsMuted(prev => !prev);
          return true;
        case 'f':
          stopEvent();
          toggleFullscreen();
          return true;
        case 'c':
          stopEvent();
          applyCaption(
            selectedCaption === 'off' ? (textTrack?.[0]?.lang ?? 'off') : 'off'
          );
          return true;
        default:
          break;
      }

      // 한/영 입력 상태와 무관하게 물리 키 기준으로 처리
      switch (code) {
        case 'keyk':
          stopEvent();
          togglePlayPause();
          return true;
        case 'keym':
          stopEvent();
          setIsMuted(prev => !prev);
          return true;
        case 'keyf':
          stopEvent();
          toggleFullscreen();
          return true;
        case 'keyc':
          stopEvent();
          applyCaption(
            selectedCaption === 'off' ? (textTrack?.[0]?.lang ?? 'off') : 'off'
          );
          return true;
        default:
          return false;
      }
    },
    [
      applyCaption,
      seekBy,
      selectedCaption,
      textTrack,
      toggleFullscreen,
      togglePlayPause,
    ]
  );

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = event => {
    handleHotkey(event);
  };

  React.useEffect(() => {
    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (!isHotkeyActive) {
        return;
      }
      const target = event.target as HTMLElement | null;
      const isTypingTarget =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        Boolean(target?.isContentEditable);
      if (isTypingTarget) {
        return;
      }
      const handled = handleHotkey(event);
      if (!handled) {
        return;
      }
    };

    window.addEventListener('keydown', onWindowKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', onWindowKeyDown, { capture: true });
    };
  }, [handleHotkey, isHotkeyActive]);

  React.useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      const wrapper = wrapperRef.current;
      const target = event.target as Node | null;
      if (!wrapper || !target) {
        return;
      }
      if (!wrapper.contains(target)) {
        setIsHotkeyActive(false);
      }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
    };
  }, []);

  const captionOptions = textTrack ?? [];
  const rootClassName = ['elice-video-v3', className].filter(Boolean).join(' ');
  const rootStyle: React.CSSProperties = {
    ...style,
    '--v3-primary': customColor?.primary ?? primaryColor ?? '#55d8ff',
    '--v3-primary-strong': customColor?.primary ?? primaryColor ?? '#14bde8',
    '--v3-bg': customColor?.background ?? '#07080a',
    '--v3-fg': customColor?.text ?? '#f7f9ff',
    '--v3-muted': customColor?.mutedText ?? '#b9c2d9',
    '--v3-control-bg':
      customColor?.controlBackground ?? 'rgba(12, 14, 20, 0.72)',
    '--v3-border': customColor?.border ?? 'rgba(255, 255, 255, 0.24)',
    '--v3-danger': customColor?.error ?? '#ff6b6b',
    '--v3-focus':
      customColor?.focus ?? customColor?.primary ?? primaryColor ?? '#55d8ff',
  } as React.CSSProperties;

  return (
    <div
      ref={wrapperRef}
      className={rootClassName}
      style={rootStyle}
      onPointerMove={resetControlsTimer}
      onPointerDown={() => {
        setIsHotkeyActive(true);
        wrapperRef.current?.focus();
      }}
      onMouseEnter={() => setIsHotkeyActive(true)}
      onFocus={() => setIsHotkeyActive(true)}
      onBlur={() => setIsHotkeyActive(false)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-label={labels.player}
    >
      <style>{styles}</style>
      <video
        ref={videoRef}
        poster={poster}
        autoPlay={autoPlay}
        loop={loop}
        playsInline
        muted={isMuted}
        preload={preload}
        controls={false}
      >
        {source.map(srcInfo => (
          <source key={srcInfo.src} src={srcInfo.src} type={srcInfo.type} />
        ))}
        {captionOptions.map(track => (
          <track
            key={track.src}
            src={track.src}
            kind="captions"
            srcLang={track.lang}
            label={track.label}
            default={track.default}
          />
        ))}
      </video>

      <div className="elice-video-v3__top" />
      <div className="elice-video-v3__top-actions">
        {captionOptions.length > 0 ? (
          <select
            data-video-v3-control="true"
            className="elice-video-v3__select"
            value={selectedCaption}
            onChange={event => {
              applyCaption(event.target.value);
            }}
            aria-label={labels.captions}
            title={labels.captions}
          >
            <option value="off">{labels.captionsOff}</option>
            {captionOptions.map(track => (
              <option key={track.src} value={track.lang}>
                {track.label ?? track.lang}
              </option>
            ))}
          </select>
        ) : null}
        <select
          data-video-v3-control="true"
          className="elice-video-v3__select"
          value={String(playbackRate)}
          onChange={event => {
            setPlaybackRate(Number(event.target.value));
          }}
          aria-label={labels.speed}
          title={labels.speed}
        >
          {playbackRates.map(rate => (
            <option key={rate} value={String(rate)}>
              {rate}x
            </option>
          ))}
        </select>
      </div>
      <div
        className="elice-video-v3__surface"
        onPointerDown={handleSurfacePointerDown}
      >
        <div className="elice-video-v3__gesture-hit elice-video-v3__gesture-hit--left" />
        <div className="elice-video-v3__gesture-hit elice-video-v3__gesture-hit--center" />
        <div className="elice-video-v3__gesture-hit elice-video-v3__gesture-hit--right" />
      </div>

      {!isReady || isBuffering ? (
        <div className="elice-video-v3__loader" aria-label={labels.loading} />
      ) : null}

      {errorMessage ? (
        <div className="elice-video-v3__error">{errorMessage}</div>
      ) : null}
      {feedbackText ? (
        <div className="elice-video-v3__feedback">{feedbackText}</div>
      ) : null}
      {!isPlaying ? (
        <button
          data-video-v3-control="true"
          className="elice-video-v3__center-play"
          type="button"
          onClick={togglePlayPause}
          aria-label={labels.play}
          title={labels.play}
        >
          <IconPlay />
        </button>
      ) : null}

      <div
        className="elice-video-v3__controls"
        data-hidden={showControls ? 'false' : 'true'}
      >
        <div
          className="elice-video-v3__timeline-wrap"
          data-video-v3-control="true"
        >
          <input
            data-video-v3-control="true"
            className="elice-video-v3__timeline"
            type="range"
            min={0}
            max={Number.isFinite(duration) && duration > 0 ? duration : 0}
            step={0.1}
            value={Math.min(
              currentTime,
              Number.isFinite(duration) ? duration : currentTime
            )}
            onPointerDown={() => {
              isDraggingTimelineRef.current = true;
            }}
            onPointerUp={() => {
              isDraggingTimelineRef.current = false;
            }}
            onChange={event => {
              const next = Number(event.target.value);
              if (!Number.isFinite(next)) {
                return;
              }
              setCurrentTime(next);
              if (videoRef.current) {
                videoRef.current.currentTime = next;
              }
            }}
            aria-label={labels.seek}
          />
        </div>

        <div className="elice-video-v3__row">
          <div className="elice-video-v3__group">
            <button
              data-video-v3-control="true"
              className="elice-video-v3__btn"
              type="button"
              onClick={togglePlayPause}
              aria-label={isPlaying ? labels.pause : labels.play}
              title={isPlaying ? labels.pause : labels.play}
            >
              {isPlaying ? <IconPause /> : <IconPlay />}
            </button>

            <button
              data-video-v3-control="true"
              className="elice-video-v3__btn"
              type="button"
              onClick={() => {
                setIsMuted(prev => !prev);
              }}
              aria-label={isMuted ? labels.unmute : labels.mute}
              title={isMuted ? labels.unmute : labels.mute}
            >
              {isMuted ? <IconVolumeOff /> : <IconVolumeOn />}
            </button>

            <input
              data-video-v3-control="true"
              className="elice-video-v3__volume elice-video-v3__hide-mobile"
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={isMuted ? 0 : volume}
              onChange={event => {
                const next = Number(event.target.value);
                setVolume(next);
                setIsMuted(next <= 0);
              }}
              aria-label="Volume"
            />

            <div className="elice-video-v3__time elice-video-v3__hide-mobile">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          <div className="elice-video-v3__group elice-video-v3__group--right">
            {captionOptions.length > 0 ? (
              <select
                data-video-v3-control="true"
                className="elice-video-v3__select elice-video-v3__bottom-hide-small"
                value={selectedCaption}
                onChange={event => {
                  applyCaption(event.target.value);
                }}
                aria-label={labels.captions}
                title={labels.captions}
              >
                <option value="off">{labels.captionsOff}</option>
                {captionOptions.map(track => (
                  <option key={track.src} value={track.lang}>
                    {track.label ?? track.lang}
                  </option>
                ))}
              </select>
            ) : null}

            <select
              data-video-v3-control="true"
              className="elice-video-v3__select elice-video-v3__bottom-hide-small"
              value={String(playbackRate)}
              onChange={event => {
                setPlaybackRate(Number(event.target.value));
              }}
              aria-label={labels.speed}
              title={labels.speed}
            >
              {playbackRates.map(rate => (
                <option key={rate} value={String(rate)}>
                  {rate}x
                </option>
              ))}
            </select>

            <button
              data-video-v3-control="true"
              className="elice-video-v3__btn"
              type="button"
              onClick={() => {
                void togglePiP();
              }}
              title={labels.pip}
              aria-label={labels.pip}
            >
              <IconPiP />
            </button>

            <button
              data-video-v3-control="true"
              className="elice-video-v3__btn"
              type="button"
              onClick={toggleFullscreen}
              title={isFullscreen ? labels.exitFullscreen : labels.fullscreen}
              aria-label={
                isFullscreen ? labels.exitFullscreen : labels.fullscreen
              }
            >
              {isFullscreen ? <IconFullscreenExit /> : <IconFullscreen />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
